import React, { useState, useCallback, useRef, lazy, Suspense } from "react";
import { Box, Backdrop, CircularProgress } from "@mui/material";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { closeArtifactPanel } from "../midasPageSlice";
import { dt } from "../../../shared/themes/redesignTokens";

// Lazy-load the artifact panel (F6.10)
const MidasArtifactPanel = lazy(() => import("./MidasArtifactPanel"));

// ===================================================================
//  Constants
// ===================================================================

const SHEET_MIN_VH = 40;
const SHEET_MAX_VH = 80;
const DISMISS_THRESHOLD = 100; // px downward to dismiss
const SWIPE_UP_THRESHOLD = 60; // px upward to expand

// ===================================================================
//  MidasPhoneArtifactSheet -- bottom sheet for phone artifacts (F6.3)
//  Default: 40vh. Swipe up to 80vh. Swipe down to dismiss.
//  F6.4: slide-up animation, F6.7: reduced motion, F6.8: ARIA
// ===================================================================

interface MidasPhoneArtifactSheetProps {
  prefersReducedMotion?: boolean;
  /** Optional callback for dismiss — if provided, used instead of Redux dispatch.
   *  This allows the kiosk chat to manage artifact state locally. */
  onDismiss?: () => void;
}

const MidasPhoneArtifactSheet: React.FC<MidasPhoneArtifactSheetProps> = ({ prefersReducedMotion = false, onDismiss }) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  const [sheetHeight, setSheetHeight] = useState(SHEET_MIN_VH);
  const [isDragging, setIsDragging] = useState(false);

  // Touch tracking refs
  const touchStartY = useRef(0);
  const touchStartHeight = useRef(SHEET_MIN_VH);

  const handleDismiss = useCallback(() => {
    if (onDismiss) {
      onDismiss();
    } else {
      dispatch(closeArtifactPanel());
    }
  }, [dispatch, onDismiss]);

  // ── Touch handlers for drag handle ──

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
      touchStartHeight.current = sheetHeight;
      setIsDragging(true);
    },
    [sheetHeight]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging) return;
      const currentY = e.touches[0].clientY;
      const deltaY = touchStartY.current - currentY; // positive = swipe up
      const vh = window.innerHeight / 100;
      const deltaVh = deltaY / vh;
      const newHeight = Math.max(
        10,
        Math.min(SHEET_MAX_VH, touchStartHeight.current + deltaVh)
      );
      setSheetHeight(newHeight);
    },
    [isDragging]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    const deltaFromStart = sheetHeight - touchStartHeight.current;

    // Swipe down past threshold: dismiss
    if (deltaFromStart < -(DISMISS_THRESHOLD / (window.innerHeight / 100))) {
      handleDismiss();
      setSheetHeight(SHEET_MIN_VH); // reset for next open
      return;
    }

    // Snap to nearest detent
    const midpoint = (SHEET_MIN_VH + SHEET_MAX_VH) / 2;
    if (sheetHeight > midpoint) {
      setSheetHeight(SHEET_MAX_VH);
    } else {
      setSheetHeight(SHEET_MIN_VH);
    }
  }, [sheetHeight, handleDismiss]);

  return (
    <>
      {/* Dimmed backdrop */}
      <Backdrop
        open
        onClick={handleDismiss}
        sx={{
          zIndex: 1200,
          backgroundColor: "rgba(0,0,0,0.4)",
          transition: prefersReducedMotion ? "none" : "opacity 0.25s ease",
        }}
      />

      {/* Bottom sheet — F6.4: slide-up animation, F6.8: ARIA */}
      <Box
        role="dialog"
        aria-label={translate("midas.aria.bottomSheet") || "Artifact bottom sheet"}
        aria-modal="true"
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          height: `${sheetHeight}vh`,
          zIndex: 1201,
          display: "flex",
          flexDirection: "column",
          backgroundColor: dt.cardBg,
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
          transition: isDragging
            ? "none"
            : prefersReducedMotion
              ? "none"
              : "height 250ms ease-out",
          animation: prefersReducedMotion
            ? "none"
            : "midasSlideUpSheet 0.3s cubic-bezier(0.4, 0, 0.2, 1) both",
          overflow: "hidden",
        }}
      >
        {/* Drag handle bar */}
        <Box
          role="separator"
          aria-label={translate("midas.aria.dragHandle") || "Drag to resize"}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "28px",
            flexShrink: 0,
            cursor: "grab",
            touchAction: "none",
          }}
        >
          <Box
            sx={{
              width: "36px",
              height: "4px",
              borderRadius: "2px",
              backgroundColor: dt.textMuted,
              opacity: 0.5,
            }}
          />
        </Box>

        {/* Artifact panel content */}
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <Suspense
            fallback={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <CircularProgress
                  size={24}
                  sx={{ color: dt.textMuted }}
                />
              </Box>
            }
          >
            <MidasArtifactPanel />
          </Suspense>
        </Box>
      </Box>
    </>
  );
};

export default MidasPhoneArtifactSheet;
